import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import SolidAngleRightIcon from 'LuxKit/Icons/solid/SolidAngleRightIcon'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import EmptyMustDoImage from '../EmptyMustDoImage'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import Group from 'components/utils/Group'

const IconContainer = styled.div`
  grid-area: icon; 
  opacity: 0;
  justify-self: end;
  align-self: center;

  transition: opacity 200ms;
  ${mediaQueryUp.tablet} {
    position: absolute;
    grid-area: unset;
    top: ${rem(20)};
    right: ${rem(16)};
  }
`

const Container = styled(Clickable)`
  background: ${props => props.theme.palette.neutral.default.seven};
  box-shadow: 0 0 0 ${rem(1)} ${props => props.theme.palette.neutral.default.six};
  border-radius: ${props => props.theme.borderRadius.L};
  position: relative;
  display: grid;
  gap: ${rem(16)};
  padding-right: ${rem(12)};
  grid-template: "image content icon" auto / ${rem(80)} 1fr auto;
  transition: box-shadow 200ms;

  &.selected {
    box-shadow: 0 0 0 ${rem(2)} ${props => props.theme.palette.neutral.default.one};
    > ${IconContainer} {
      opacity: 1;
    }
  }

  ${mediaQueryUp.tablet} {
    padding: 0;
    gap: 0;
    grid-template:
      "image" ${rem(80)}
      "content" auto / auto;
    grid-row-gap: ${rem(12)};

    > ${IconContainer} {
      align-self: self-start;
    }

    &.layout-horizontal {
      gap: ${rem(16)};
      padding-right: ${rem(12)};
      grid-template: "image content icon" auto / auto 1fr auto;

      > ${IconContainer} {
        align-self: center;
      }
    }
  }

  ${mediaHoverable} {
    &:hover {
      box-shadow: 0 0 0 ${rem(2)} ${props => props.theme.palette.neutral.default.one};
      > ${IconContainer} {
        opacity: 1;
      }
    }
  }
  
  &:focus {
    ${interactiveFocusStyle}
  }
`

const ImageContainer = styled.div`
  grid-area: image;
  display: flex;
  overflow: hidden;
  align-self: stretch;
  border-radius:  ${props => props.theme.borderRadius.L} 0 0  ${props => props.theme.borderRadius.L};
  /* Default mobile + desktop horizontal */

  ${mediaQueryUp.tablet} {
    align-self: stretch;
    border-radius:  ${props => props.theme.borderRadius.L} ${props => props.theme.borderRadius.L} 0 0;
  }
`

const ContentContainer = styled(Group)`
  grid-area: content;
  margin: ${rem(16)} 0;

  ${mediaQueryUp.tablet} {
    margin: 0 ${rem(16)} ${rem(20)} ${rem(16)};
  }
`

const AllowHyphenationBodyText = styled(BodyText)`
  hyphens: auto;
`

interface Props<Value> {
  title: string
  subtitle?: string
  imageId?: string
  imageUrl?: string
  /**
   * @default auto
   */
  layout?: 'auto' | 'horizontal'
  interactionIcon?: 'right' | 'check'
  selected?: boolean
  value: Value
  disabled?: boolean
  onClick: (value: Value) => void
}

function ImageTileCard<Value>({
  layout = 'auto',
  title,
  subtitle,
  imageId,
  imageUrl,
  selected,
  value,
  disabled,
  interactionIcon = 'check',
  onClick,
}: Props<Value>) {
  const handleClick = useCallback(() => {
    onClick(value)
  }, [value, onClick])

  return (
    <Container
      className={clsx(`layout-${layout}`, { selected })}
      onClick={handleClick}
      disabled={disabled}
    >
      <ImageContainer>
        {!!imageId && <Image
          alt={title}
          id={imageId}
          fit="center"
          loading="lazy"
        />}
        {!imageId && !!imageUrl && <Image
          alt={title}
          image={{ url: imageUrl, title }}
          fit="center"
          loading="lazy"
        />}
        {!imageId && !imageUrl && <EmptyMustDoImage />}
      </ImageContainer>

      <ContentContainer direction="vertical" verticalAlign="center" gap={2}>
        <Heading variant="heading6" align="start" lineClamp={2} wrap="word-wrap"> {title} </Heading>
        {!!subtitle && <AllowHyphenationBodyText variant="medium" align="start" lineClamp={3} colour="neutral-three" wrap="word-wrap"> {subtitle} </AllowHyphenationBodyText>}
      </ContentContainer>

      {selected && <IconContainer>
        {interactionIcon === 'check' && <SolidCheckCircleIcon size={20} />}
        {interactionIcon === 'right' && <SolidAngleRightIcon size={20} />}
      </IconContainer>}
    </Container>
  )
}

export default ImageTileCard
